import { useState, useEffect, useRef, FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { verifyEmailToken, resendVerificationEmail } from '../api/auth';
import { validateEmail } from '../utils/validation';
import { Spinner } from '../components/Spinner';

const REDIRECT_SECONDS = 5;

type PageState = 'initial' | 'submitting' | 'success' | 'error';

/**
 * Verification landing page for the emailed link (`/verify-email?token=...`).
 *
 * Deliberately does NOT call the backend automatically on page load — only an explicit "Confirm
 * your email" button click fires `POST /api/auth/verify-email`. This is what defeats mail-security
 * scanners that pre-fetch every link in an email: a scanner fetching this page only ever touches
 * inert HTML, since Brevo (unlike SendGrid) has no way to fully disable click tracking on
 * transactional email. See SF-102 spec, Component 5/9.
 */
export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState<PageState>(token ? 'initial' : 'error');
  const [errorMessage, setErrorMessage] = useState(
    token ? '' : 'This verification link is missing a token.'
  );
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [isResending, setIsResending] = useState(false);

  const redirectTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clear the countdown/redirect timer on unmount so it can't fire after navigating away.
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearInterval(redirectTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (state !== 'success') {
      return;
    }
    redirectTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (redirectTimerRef.current) {
            clearInterval(redirectTimerRef.current);
          }
          navigate('/?login=true');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (redirectTimerRef.current) {
        clearInterval(redirectTimerRef.current);
      }
    };
  }, [state, navigate]);

  const handleConfirm = async () => {
    if (!token || state === 'submitting') {
      return;
    }
    setState('submitting');

    const { error } = await verifyEmailToken(token);
    if (error) {
      setErrorMessage('This verification link is invalid or has expired.');
      setState('error');
      return;
    }

    setState('success');
  };

  const handleResend = async (e: FormEvent) => {
    e.preventDefault();
    setResendError('');
    setResendMessage('');

    if (!validateEmail(resendEmail)) {
      setResendError('Please enter a valid email address');
      return;
    }

    setIsResending(true);
    const { error } = await resendVerificationEmail(resendEmail);
    setIsResending(false);

    if (error) {
      setResendError(error);
      return;
    }

    setResendMessage('If an account exists for this email, a new verification link has been sent.');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: '#110f0d' }}
    >
      <div
        className="relative w-full max-w-[420px]"
        style={{
          background: '#1c1916',
          border: '1px solid #2e2924',
          borderRadius: 8,
          padding: '36px 32px',
        }}
      >
        <div
          className="absolute top-[-1px] left-6 right-6 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent, #6a9e8e, transparent)' }}
        />

        <h1
          className="text-center mb-6"
          style={{ fontFamily: "'Cinzel', serif", fontSize: 20, fontWeight: 600, color: '#f0e8dc' }}
        >
          Verify Your Email
        </h1>

        {(state === 'initial' || state === 'submitting') && (
          <div className="text-center">
            <p className="mb-6" style={{ color: '#b5a99c', fontSize: 14 }}>
              Click the button below to confirm your email address.
            </p>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={state === 'submitting'}
              className="w-full py-3 rounded font-semibold transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #2a4a4a, #1e3a3a)',
                color: '#fff',
                fontFamily: "'Cinzel', serif",
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '0.06em',
                border: '1px solid rgba(106,158,142, 0.3)',
                cursor: state === 'submitting' ? 'not-allowed' : 'pointer',
              }}
            >
              {state === 'submitting' ? (
                <>
                  <Spinner size="sm" /> Confirming...
                </>
              ) : (
                'Confirm your email'
              )}
            </button>
          </div>
        )}

        {state === 'success' && (
          <div className="text-center">
            <p className="mb-4" style={{ color: '#f0e8dc', fontSize: 15 }}>
              Email verified — redirecting to login in {countdown}…
            </p>
            <Link
              to="/?login=true"
              className="hover:underline"
              style={{ color: '#6a9e8e', fontSize: 14 }}
            >
              Log in now
            </Link>
          </div>
        )}

        {state === 'error' && (
          <div>
            <div
              className="mb-5 px-3 py-2 rounded text-sm text-center"
              style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}
            >
              {errorMessage}
            </div>

            <p className="mb-3 text-center" style={{ color: '#b5a99c', fontSize: 14 }}>
              Enter your email to request a new verification link.
            </p>
            <form onSubmit={handleResend}>
              <div className="mb-4">
                <input
                  type="email"
                  placeholder="player@example.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  disabled={isResending}
                  className="w-full px-4 py-3 rounded focus:outline-none transition-colors"
                  style={{
                    background: '#0a0908',
                    border: '1px solid #2e2924',
                    color: '#f0e8dc',
                    fontSize: 16,
                  }}
                />
              </div>

              {resendError && (
                <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#2a1414', border: '1px solid #4a2020', color: '#ff5555' }}>
                  {resendError}
                </div>
              )}
              {resendMessage && (
                <div className="mb-4 px-3 py-2 rounded text-sm" style={{ background: '#14241a', border: '1px solid #205a3a', color: '#6a9e8e' }}>
                  {resendMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isResending}
                className="w-full py-3 rounded font-semibold transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #2a4a4a, #1e3a3a)',
                  color: '#fff',
                  fontFamily: "'Cinzel', serif",
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  border: '1px solid rgba(106,158,142, 0.3)',
                  cursor: isResending ? 'not-allowed' : 'pointer',
                }}
              >
                {isResending ? <><Spinner size="sm" /> Sending...</> : 'Resend verification email'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
